import type {
  MarketLeaderboardMetric,
  MarketLeaderboard,
  ProviderHistory,
} from "../api/hip3";
import { normalizeMarketIdForRoute } from "../api/hip3";
import type { Hip3Market } from "../types/hip3";
import { marketNotionalOi } from "./market-utils";

export type ProviderPointRow = {
  t: string;
  [provider: string]: number | string | null;
};

export type FundingInstabilityRegime = "Stable" | "Elevated" | "Unstable" | "Extreme";

export type NormalizedLeaderboardRow = {
  rank: number;
  provider: string;
  marketId: string;
  label: string;
  metric: MarketLeaderboardMetric;
  metricValue: number | null;
  fundingMean: number | null;
  fundingInstabilityRegime: FundingInstabilityRegime | null;
  fundingInstabilityScore: number | null;
};

export function fundingInstabilityRegime(
  fundingStddev: number | null | undefined
): FundingInstabilityRegime | null {
  if (typeof fundingStddev !== "number" || !Number.isFinite(fundingStddev)) {
    return null;
  }

  const bps = Math.abs(fundingStddev) * 10_000;
  if (bps < 1) return "Stable";
  if (bps < 3) return "Elevated";
  if (bps < 8) return "Unstable";
  return "Extreme";
}

export function shapeProviderDominanceSeries(series: ProviderHistory["series"]): ProviderPointRow[] {
  const byTime = new Map<string, ProviderPointRow>();

  for (const providerSeries of series) {
    for (const point of providerSeries.points) {
      if (!byTime.has(point.t)) {
        byTime.set(point.t, { t: point.t });
      }

      const row = byTime.get(point.t);
      if (!row) {
        continue;
      }

      row[providerSeries.provider] = point.value;
    }
  }

  return Array.from(byTime.values()).sort((a, b) =>
    String(a.t).localeCompare(String(b.t))
  );
}

export function normalizeLeaderboardRows(payload: MarketLeaderboard): NormalizedLeaderboardRow[] {
  const normalized = payload.markets.slice(0, 10).map((row, index) => {
    let metricValue: number | null = null;

    if (payload.meta.metric === "oiGrowth") metricValue = row.oiChangePct;
    if (payload.meta.metric === "volumeGrowth") metricValue = row.snapshotVolume24hChangePct;
    if (payload.meta.metric === "spreadTightest" || payload.meta.metric === "spreadWidest") {
      metricValue = row.spreadAvgBps;
    }
    if (payload.meta.metric === "fundingHighest") metricValue = row.fundingMean;
    if (payload.meta.metric === "fundingVolatility") metricValue = row.fundingStddev;

    return {
      rank: index + 1,
      provider: row.provider,
      marketId: normalizeMarketIdForRoute(row.provider, row.marketId),
      label: `${row.baseAsset} / ${row.quoteAsset}`,
      metric: payload.meta.metric,
      metricValue,
      fundingMean: row.fundingMean,
      fundingInstabilityRegime: null,
      fundingInstabilityScore: null,
    };
  });

  if (payload.meta.metric !== "fundingVolatility") {
    return normalized;
  }

  const maxStddev = normalized.reduce((max, row) => {
    if (typeof row.metricValue !== "number" || !Number.isFinite(row.metricValue)) {
      return max;
    }
    return Math.max(max, Math.abs(row.metricValue));
  }, 0);

  return normalized.map((row) => {
    if (typeof row.metricValue !== "number" || !Number.isFinite(row.metricValue)) {
      return row;
    }

    return {
      ...row,
      fundingInstabilityRegime: fundingInstabilityRegime(row.metricValue),
      fundingInstabilityScore: maxStddev > 0 ? Math.abs(row.metricValue) / maxStddev : null,
    };
  });
}

export function metricLabel(metric: MarketLeaderboardMetric): string {
  if (metric === "oiGrowth") return "OI Growth";
  if (metric === "volumeGrowth") return "Volume Growth";
  if (metric === "spreadTightest") return "Spread (Tightest)";
  if (metric === "spreadWidest") return "Spread (Widest)";
  if (metric === "fundingHighest") return "Funding";
  return "Funding Instability";
}

export function leaderboardValueHeader(metric: MarketLeaderboardMetric): string {
  if (metric === "oiGrowth") return "OI Growth";
  if (metric === "volumeGrowth") return "Volume Growth";
  if (metric === "spreadTightest" || metric === "spreadWidest") return "Spread";
  if (metric === "fundingHighest") return "Funding";
  return "Instability (bps)";
}

export function aggregateByProvider(markets: Hip3Market[]) {
  const providerMap = new Map<
    string,
    {
      provider: string;
      marketCount: number;
      assetSet: Set<string>;
      oiUsd: number;
      volume24h: number;
      spreadSum: number;
      spreadCount: number;
      fundingSum: number;
      fundingCount: number;
    }
  >();

  for (const market of markets) {
    const key = market.provider;
    if (!providerMap.has(key)) {
      providerMap.set(key, {
        provider: key,
        marketCount: 0,
        assetSet: new Set<string>(),
        oiUsd: 0,
        volume24h: 0,
        spreadSum: 0,
        spreadCount: 0,
        fundingSum: 0,
        fundingCount: 0,
      });
    }

    const row = providerMap.get(key);
    if (!row) continue;

    row.marketCount += 1;
    row.assetSet.add(market.baseAsset);
    row.oiUsd += marketNotionalOi(market);
    row.volume24h += market.volume24h ?? 0;

    if (typeof market.spreadBps === "number" && Number.isFinite(market.spreadBps)) {
      row.spreadSum += market.spreadBps;
      row.spreadCount += 1;
    }

    if (typeof market.funding === "number" && Number.isFinite(market.funding)) {
      row.fundingSum += market.funding;
      row.fundingCount += 1;
    }
  }

  return Array.from(providerMap.values()).map((row) => ({
    provider: row.provider,
    markets: row.marketCount,
    assets: row.assetSet.size,
    oiUsd: row.oiUsd,
    volume24h: row.volume24h,
    spreadAvgBps: row.spreadCount > 0 ? row.spreadSum / row.spreadCount : null,
    fundingAvg: row.fundingCount > 0 ? row.fundingSum / row.fundingCount : null,
  }));
}

export function aggregateAssetProviderComparisons(markets: Hip3Market[], asset: string) {
  const filtered = markets.filter((market) => market.baseAsset === asset);
  const grouped = aggregateByProvider(filtered);

  return {
    oiByProvider: grouped.map((row) => ({ provider: row.provider, value: row.oiUsd })),
    spreadByProvider: grouped.map((row) => ({ provider: row.provider, value: row.spreadAvgBps })),
    fundingByProvider: grouped.map((row) => ({ provider: row.provider, value: row.fundingAvg })),
  };
}

export function normalizeProviderMetricBars(
  bars: Array<{ provider: string; value: number | null }>
): Array<{ provider: string; value: number }> {
  return bars
    .filter(
      (bar): bar is { provider: string; value: number } =>
        typeof bar.value === "number" && Number.isFinite(bar.value)
    )
    .sort((a, b) => b.value - a.value);
}
